"use client";

import { hexToHsva } from "@noya-app/noya-color";
import React, { useCallback, useState } from "react";
import useEyeDropper from "use-eye-dropper";
import { useColorPicker } from "../contexts/ColorPickerContext";

type DropperError = {
  message: string;
  canceled?: boolean;
};

const isError = <T,>(err: DropperError | T): err is DropperError =>
  !!err && err instanceof Error && !!err.message;

const isNotCanceled = <T,>(err: DropperError | T): err is DropperError =>
  isError(err) && !err.canceled;

export function EyeDropper(
  props: React.DetailedHTMLProps<
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    HTMLButtonElement
  >
) {
  const [_hsva, onChange] = useColorPicker();
  const { open } = useEyeDropper();
  const [_error, setError] = useState<DropperError>();
  // useEyeDropper will reject/cleanup the open() promise on unmount,
  // so setState never fires when the component is unmounted.
  const pickColor = useCallback(() => {
    // Using async/await (can be used as a promise as-well)
    const openPicker = async () => {
      try {
        const color = await open();
        onChange(hexToHsva(color.sRGBHex));
      } catch (e) {
        console.log(e);
        // Ensures component is still mounted
        // before calling setState
        if (isNotCanceled(e)) {
          setError(e);
        }
      }
    };
    openPicker();
  }, [open]);

  return (
    <button
      type="button"
      onClick={pickColor}
      style={{
        appearance: "none",
        borderRadius: "4px",
        boxShadow: "0 0 0 1px rgba(0,0,0,0.2) inset",
        backgroundColor: "rgba(0,0,0,0.02)",
        // width: "20px",
        // height: "20px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "4px",
        color: "#888",
        border: "none",
        ...props.style,
      }}
      {...props}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="13px"
        height="13px"
        viewBox="0 0 15 15"
        // style={{
        //   transform: "scale(0.9)",
        //   transformOrigin: "center",
        // }}
      >
        <path
          fill="currentColor"
          d="m.5 12.5l-.354-.354l-.146.147v.207zM8 5l.354-.354a.5.5 0 0 0-.708 0zm2 2l.354.354a.5.5 0 0 0 0-.708zm-7.5 7.5v.5h.207l.147-.146zm-2 0H0a.5.5 0 0 0 .5.5zm7-10l-.354-.354l-.353.354l.353.354zm3.086-3.086l-.354-.353zm2.828 0l-.353.354zm.172.172l.353-.354zm0 2.828l-.354-.353zM10.5 7.5l-.354.354l.354.353l.354-.353zM.854 12.854l7.5-7.5l-.708-.708l-7.5 7.5zm6.792-7.5l2 2l.708-.708l-2-2zm2 1.292l-7.5 7.5l.708.708l7.5-7.5zM2.5 14h-2v1h2zm-1.5.5v-2H0v2zM6.146 3.854l5 5l.708-.708l-5-5zm1.708 1l3.085-3.086l-.707-.707l-3.086 3.085zm5.207-3.086l.171.171l.707-.707l-.171-.171zm.171 2.293l-3.086 3.085l.708.708l3.085-3.086zm-2.378 3.085l-3-3l-.708.708l3 3zm2.378-5.207a1.5 1.5 0 0 1 0 2.122l.707.707a2.5 2.5 0 0 0 0-3.536zm-2.293-.171a1.5 1.5 0 0 1 2.122 0l.707-.707a2.5 2.5 0 0 0-3.536 0z"
        />
      </svg>
    </button>
  );
}
